import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { Product } from 'public-contracts';
import { productsService } from '../services/productsService';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { ShoppingBag, Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../utils';

interface ProductComboboxProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  allowNone?: boolean;
  noneLabel?: string;
  'data-testid'?: string;
}

export const ProductCombobox: React.FC<ProductComboboxProps> = ({
  value,
  onValueChange,
  placeholder,
  disabled = false,
  className,
  allowNone = false,
  noneLabel,
  'data-testid': dataTestId,
}) => {
  const { translate } = useTranslation();
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const loadProducts = React.useCallback(async () => {
    try {
      setIsLoading(true);
      // Load all products - Command component will handle client-side filtering
      const data = await productsService.getAllProducts({ maxSize: 1000 });
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load products:', err);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load all products when popover opens (only once)
  useEffect(() => {
    if (open && products.length === 0) {
      loadProducts();
    }
  }, [open, products.length, loadProducts]);

  // Auto-focus search input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const selectedProduct = products.find((prod) => prod.id === value);

  const handleSelect = (selectedValue: string) => {
    if (selectedValue === '__none__') {
      onValueChange('');
    } else {
      onValueChange(selectedValue);
    }
    setOpen(false);
  };

  // Format product display: name + last 4 characters of ID
  const formatProductDisplay = (product: Product) => {
    const name = (product as any).name || product.reference || product.id;
    const idSuffix = product.id.slice(-4);
    return `${name} (${idSuffix})`;
  };

  // Get search value for Command component (searchable by name and ID)
  const getSearchValue = (product: Product) => {
    const name = (product as any).name || product.reference || product.id;
    return `${name} ${product.reference || ''} ${product.id}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', className)}
          disabled={disabled}
          data-testid={dataTestId}
        >
          <span className="flex items-center gap-2 truncate">
            {value && selectedProduct ? (
              <>
                <ShoppingBag className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  {formatProductDisplay(selectedProduct)}
                </span>
              </>
            ) : allowNone && !value ? (
              <span className="text-muted-foreground">
                {noneLabel ||
                  translate('pos.inventory.product.none', 'No product')}
              </span>
            ) : (
              <span className="text-muted-foreground">
                {placeholder ||
                  translate(
                    'pos.inventory.product.placeholder',
                    'Select product (optional)'
                  )}
              </span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[400px] p-0"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => {
            searchInputRef.current?.focus();
          }, 0);
        }}
      >
        <Command>
          <CommandInput
            ref={searchInputRef}
            placeholder={translate(
              'pos.inventory.product.searchPlaceholder',
              'Search products...'
            )}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading
                ? translate('common.loading', 'Loading...')
                : translate('common.noResults', 'No products found.')}
            </CommandEmpty>
            <CommandGroup>
              {allowNone && (
                <CommandItem
                  value="__none__"
                  onSelect={() => handleSelect('__none__')}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      !value ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="text-muted-foreground">
                    {noneLabel ||
                      translate('pos.inventory.product.none', 'No product')}
                  </span>
                </CommandItem>
              )}
              {products.map((product) => (
                <CommandItem
                  key={product.id}
                  value={getSearchValue(product)}
                  onSelect={() => handleSelect(product.id)}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === product.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <div className="flex items-center space-x-2">
                    <ShoppingBag className="h-4 w-4" />
                    <span>{formatProductDisplay(product)}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

